"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Tag } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


declare const wvcClient: any;



function PostDate({ date }: { date?: string }) {
  if (!date) return null;
  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  return (
    <div data-section-id="33039" className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
      <Calendar className="h-4 w-4 text-primary" />
      <span>{formattedDate}</span>
    </div>
  );
}

function PostAuthor({ author }: { author?: Post["author"] }) {
  if (!author) return null;
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
      <User className="h-4 w-4 text-primary" />
      <span>{author.name}</span>
    </div>
  );
}

function PostCategories({ categories }: { categories?: Post["categories"] }) {
  if (!categories || categories.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {categories.map((cat, i) => (
        <span
          key={cat.id}
          data-index={i}
          className="inline-flex items-center rounded-[5px] bg-primary px-2.5 py-0.5 text-xs font-bold uppercase tracking-widest text-background"
        >
          {cat.name}
        </span>
      ))}
    </div>
  );
}



function RelatedPostsConsumer({ currentPostId }: { currentPostId: string }) {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 animate-pulse rounded-[5px] bg-secondary/50" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden border-primary/15 bg-secondary/40 shadow-none transition-all hover:border-primary/40 rounded-[5px]">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          )}
          <CardHeader className="p-5">
            <div className="mb-2 flex flex-wrap gap-2">
              {post.categories?.slice(0, 1).map((cat, idx) => (
                <span key={idx} data-index={idx} className="text-xs font-bold uppercase tracking-widest text-primary">
                  {cat.name}
                </span>
              ))}
            </div>
            <CardTitle className="line-clamp-2 text-lg font-bold uppercase tracking-tight leading-tight font-logo text-foreground">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="grow px-5 pb-0">
            <div
              className="line-clamp-3 text-sm text-muted-foreground font-logo leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="p-5 pt-4">
            <Link to={post.link || "#"} className="group flex items-center text-xs font-bold uppercase tracking-widest text-primary">
              Discover More <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-1" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}



function RelatedPostsSection({ currentPost }: { currentPost: Post }) {
  
  
  const categoryIds = currentPost.categories?.map((c) => c.id) || [];
  
  const relatedQueryArgs: any = {
    post_type: "post", 
    posts_per_page: 3,
    post__not_in: [currentPost.id],
    orderby: "date",
    order: "DESC",
  };

  if (categoryIds.length > 0) {
    relatedQueryArgs.category__in = categoryIds;
  }

  const wp_query = new WP_Query(relatedQueryArgs);

  return (
    <div className="mt-16 border-t border-primary/20 pt-16">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-bold uppercase tracking-tighter text-primary md:text-3xl font-logo">Exclusive Insights</h3>
        <Link to="#">
          <Button variant="ghost" className="hidden sm:flex text-primary hover:bg-primary/10 rounded-[5px] uppercase font-bold tracking-widest text-xs">
            View All News <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer currentPostId={currentPost.id} />
      </PostsProvider>
      <div className="mt-8 flex justify-center sm:hidden">
        <Link to="#">
          <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-background rounded-[5px] uppercase font-bold tracking-widest text-xs">
            View All News
          </Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-8 py-12">
        <div className="h-12 w-3/4 animate-pulse rounded-[5px] bg-secondary/50" />
        <div className="flex gap-4">
          <div className="h-6 w-24 animate-pulse rounded-[5px] bg-secondary/50" />
          <div className="h-6 w-24 animate-pulse rounded-[5px] bg-secondary/50" />
        </div>
        <div className="aspect-video w-full animate-pulse rounded-[5px] bg-secondary/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded-[5px] bg-secondary/50" />
          <div className="h-4 w-full animate-pulse rounded-[5px] bg-secondary/50" />
          <div className="h-4 w-2/3 animate-pulse rounded-[5px] bg-secondary/50" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold uppercase tracking-widest text-destructive">An error occurred</h2>
        <p className="mt-2 text-muted-foreground uppercase text-xs tracking-widest">We could not load the feature article.</p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4 border-primary text-primary rounded-[5px] uppercase font-bold text-xs tracking-widest">
          Try Again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold uppercase tracking-widest text-primary">Article Not Found</h2>
        <p className="mt-2 text-muted-foreground uppercase text-xs tracking-widest">The requested content is currently unavailable in the vault.</p>
        <Link to="/HomePage" className="mt-4">
          <Button className="bg-primary text-background hover:bg-primary/90 rounded-[5px] uppercase font-bold text-xs tracking-widest">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      {}
      <header className="mb-10 text-center md:mb-14">
        <div className="mb-6 flex justify-center">
          <PostCategories categories={post.categories} />
        </div>
        
        <h1 className="mb-6 text-3xl font-bold uppercase leading-tight tracking-tighter text-primary md:text-4xl lg:text-5xl font-logo">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-muted-foreground">
          <PostAuthor author={post.author} />
          <PostDate date={post.date} />
          {}
          <div className="flex items-center gap-2 text-sm uppercase tracking-wider">
            <Clock className="h-4 w-4 text-primary" />
            <span>5 Min Read</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-[5px] shadow-none border border-primary/10 md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
          />
        </div>
      )}

      {}
      <div className="mx-auto max-w-3xl">
        <div 
          className="prose prose-lg prose-neutral dark:prose-invert max-w-none 
          [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-tight [&_h2]:text-primary [&_h2]:font-logo
          [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:uppercase [&_h3]:text-primary/90 [&_h3]:font-logo
          [&_p]:mb-6 [&_p]:leading-relaxed [&_p]:text-muted-foreground [&_p]:font-logo
          [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-muted-foreground
          [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:text-muted-foreground
          [&_li]:mb-2
          [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-foreground [&_blockquote]:bg-secondary/20 [&_blockquote]:py-4 [&_blockquote]:rounded-r-[5px]
          [&_img]:rounded-[5px] [&_img]:border [&_img]:border-primary/10
          [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-primary/15 pt-8">
            <Tag className="h-4 w-4 text-primary" />
            <span className="text-sm font-bold uppercase tracking-widest text-primary">Tags:</span>
            {post.tags.map((tag, i) => (
              <span 
                key={tag.id} 
                data-index={i}
                className="rounded-[5px] bg-secondary px-2 py-1 text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:bg-primary hover:text-background"
              >
                #{tag.name}
              </span>
            ))}
          </div>
        )}

        {}
        {post.author && (
          <div className="mt-12 rounded-[5px] bg-secondary/30 border border-primary/10 p-8">
            <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
              <Avatar className="h-20 w-20 border-1 border-primary shadow-none">
                <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
                <AvatarFallback className="text-lg bg-primary text-background font-bold">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="space-y-2">
                <h3 className="text-lg font-bold uppercase tracking-tight text-primary font-logo">About {post.author.name}</h3>
                <p className="text-sm text-muted-foreground font-logo leading-relaxed">
                  Premier nightlife consultant and event strategist at Platinum Nights. 
                  Dedicated to crafting unforgettable VIP experiences and defining the future of high-end entertainment.
                </p>
              </div>
            </div>
          </div>
        )}

        {}
        <RelatedPostsSection currentPost={post} />
      </div>
    </article>
  );
}



export default function SinglePostTemplate() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="dark bg-background min-h-screen w-full text-foreground selection:bg-primary selection:text-background font-logo">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}